// Service-catalogue helpers shared by the POS till (Pos.js) and the admin order editor
// (PosSales.js). Both screens have to turn the same `app/service/activeForPos` payload into the
// same sellable options at the same prices, so this logic lives in one place — a line added
// while editing an order must be priced exactly as it would have been at checkout.
//
// These are pure functions: the catalogue they read (`globalServiceTypes`, `allServices`) is
// passed in rather than closed over, since each screen loads it into its own state.

export const DEFAULT_POS_EXPRESS_PERCENTAGE = 50;

export const parsePrice = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

export const parsePercentage = (value, fallback = 0) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

export const getExpressMultiplier = (enabled, percentage) =>
    enabled ? 1 + Math.max(0, parsePercentage(percentage, DEFAULT_POS_EXPRESS_PERCENTAGE)) / 100 : 1;

export const getExpressPrice = (basePrice, enabled, percentage) =>
    parsePrice(basePrice) * getExpressMultiplier(enabled, percentage);

export const isPerItemService = (service) =>
    service?.isPerItem === 1 || service?.isPerItem === true || service?.isPerItem === "1";

export const isCategoryOnlyService = (service) =>
    service?.isCategoryOnly === 1 || service?.isCategoryOnly === true || service?.isCategoryOnly === "1";

export const getGlobalServiceType = (globalServiceTypes, serviceTypeId) => {
    if (serviceTypeId == null) {
        return null;
    }

    return (globalServiceTypes || []).find((type) => String(type.id) === String(serviceTypeId)) || null;
};

export const getServiceTypeName = (globalServiceTypes, serviceType) => {
    const globalType = getGlobalServiceType(globalServiceTypes, serviceType?.serviceTypeId ?? serviceType?.id);
    const rawName =
        serviceType?.serviceType?.nameEn ||
        serviceType?.serviceTypeByServiceTypeId?.nameEn ||
        globalType?.nameEn ||
        serviceType?.nameEn ||
        serviceType?.name ||
        "";

    const cleanName = rawName.split("(")[0].trim();
    return cleanName || "Service Type";
};

export const getChildServicesForCategory = (item) => {
    if (!isCategoryOnlyService(item) || !Array.isArray(item?.servicesById)) {
        return [];
    }

    return item.servicesById.map((childService) => ({
        ...childService,
        parentCategoryId: item.id,
        parentCategoryNameEn: item.nameEn,
        parentCategoryNameAr: item.nameAr
    }));
};

// The sellable service types for an item, with the price the till would charge for each.
// `source` distinguishes the two catalogue shapes, which decide what a basket line carries:
// "servicePricings" lines are (parent service id + service type id); legacy "servicesById"
// lines are the child service's own id and no service type.
export const getServiceTypesForItem = (item) => {
    if (!item) {
        return [];
    }

    const pricingEntries = Array.isArray(item.servicePricings) ? item.servicePricings : [];
    if (pricingEntries.length > 0) {
        return pricingEntries
            .map((pricing) => {
                const linkedServiceType = pricing?.serviceType || pricing?.serviceTypeByServiceTypeId || {};
                const serviceTypeId = pricing?.serviceTypeId ?? linkedServiceType.id ?? pricing?.id;
                const resolvedPrice = parsePrice(pricing?.price ?? pricing?.bagPrice);

                if (serviceTypeId == null || resolvedPrice <= 0) {
                    return null;
                }

                return {
                    id: serviceTypeId,
                    serviceTypeId,
                    nameEn: linkedServiceType.nameEn || pricing?.nameEn || "",
                    nameAr: linkedServiceType.nameAr || pricing?.nameAr || "",
                    price: resolvedPrice,
                    raw: pricing,
                    source: "servicePricings"
                };
            })
            .filter(Boolean);
    }

    const legacyEntries = Array.isArray(item.servicesById) ? item.servicesById : [];
    return legacyEntries
        .map((serviceType) => {
            const resolvedPrice = parsePrice(serviceType?.price ?? serviceType?.bagPrice ?? item?.bagPrice ?? item?.price);

            if ((serviceType?.id == null && serviceType?.serviceTypeId == null) || resolvedPrice <= 0) {
                return null;
            }

            return {
                id: serviceType?.id,
                serviceTypeId: serviceType?.serviceTypeId ?? serviceType?.id,
                nameEn: serviceType?.nameEn || serviceType?.serviceType?.nameEn || "",
                nameAr: serviceType?.nameAr || serviceType?.serviceType?.nameAr || "",
                price: resolvedPrice,
                raw: serviceType,
                source: "servicesById"
            };
        })
        .filter(Boolean);
};

// The flat list of sellable items, expanding category containers (e.g. Shoes) into their
// per-item children and dropping anything with no priced service type.
export const buildDisplayItems = (items) => {
    const seenItemIds = new Set();

    return (items || [])
        .flatMap((item) => {
            if (isCategoryOnlyService(item)) {
                return getChildServicesForCategory(item);
            }

            if (!isPerItemService(item)) {
                return [];
            }

            return [item];
        })
        .filter((item) => {
            const itemKey = String(item?.id ?? "");
            if (!itemKey || seenItemIds.has(itemKey)) {
                return false;
            }

            seenItemIds.add(itemKey);
            return getServiceTypesForItem(item).length > 0;
        });
};

export const getItemCategoryLabel = (item, allServices) => {
    if (item?.parentCategoryNameEn) {
        return item.parentCategoryNameEn;
    }

    if (item?.mainServiceId != null) {
        const parentService = (allServices || []).find((service) => String(service.id) === String(item.mainServiceId));
        if (parentService?.nameEn) {
            return parentService.nameEn;
        }
    }

    return "Items";
};

// The (serviceId, serviceTypeId) pair the backend needs to identify and re-price a line,
// derived the same way the till derives it when adding to the basket.
export const toOrderLineRef = (item, serviceType) => {
    const usesNewPricingStructure = Array.isArray(item?.servicePricings) && item.servicePricings.length > 0;
    const selectedTypeId = serviceType.serviceTypeId ?? serviceType.id;

    return {
        serviceId: usesNewPricingStructure ? item.id : serviceType.id,
        serviceTypeId: usesNewPricingStructure ? selectedTypeId : undefined
    };
};
